import {ref} from "vue";
import {decode} from "./useWords.js";
import {safeFetch} from "../utils/http.js";
import {STARTING_HEARTS, MAX_HEARTS, SERIES_LENGTH} from "./gameConstants.js";

// Archive practice mode: replay a past day's puzzle run. Everything here is deliberately
// client-only. It reads puzzle data from the read-only /api/archive endpoint and never
// calls /words/complete, /words/over or /words/guess, so it cannot touch UserState or
// either leaderboard. Progress is persisted to localStorage purely so a refresh can resume.

// Module-level singleton: which past day (if any) the player is currently practising.
const activeDate = ref(null);
// Bumped on replay to force a clean remount of the same date's run.
const runNonce = ref(0);

const storageKey = (date) => `archive:${date}`;

function loadProgress(date) {
    try {
        const raw = localStorage.getItem(storageKey(date));
        if (raw) return JSON.parse(raw);
    } catch (e) {
        console.error("Failed to read archive progress", e);
    }
    return {index: 0, hearts: STARTING_HEARTS, over: false, seenLetters: []};
}

export function useArchive() {
    function playDate(date) {
        activeDate.value = date;
    }

    function exit() {
        activeDate.value = null;
    }

    function replay() {
        if (!activeDate.value) return;
        try {
            localStorage.removeItem(storageKey(activeDate.value));
        } catch (e) {
            console.error("Failed to clear archive progress", e);
        }
        runNonce.value++;
    }

    return {activeDate, runNonce, playDate, exit, replay};
}

// Adapter consumed by useGameEngine, mirroring the live adapter's shape but backed entirely
// by local state. The engine drives the same rules (hearts, win/lose) regardless.
export function createArchiveAdapter(date) {
    const currentWords = ref([]);
    const loading = ref(false);
    const loadError = ref(false);

    const progress = loadProgress(date);
    const state = {
        index: progress.index ?? 0,
        hearts: progress.hearts ?? STARTING_HEARTS,
        over: progress.over ?? false,
        seenLetters: progress.seenLetters ?? [],
    };

    function persist() {
        try {
            localStorage.setItem(storageKey(date), JSON.stringify(state));
        } catch (e) {
            console.error("Failed to persist archive progress", e);
        }
    }

    async function loadWords() {
        // Series complete (or game over past the last puzzle): nothing to load.
        if (state.index >= SERIES_LENGTH) {
            currentWords.value = [];
            return;
        }
        loading.value = true;
        loadError.value = false;
        const response = await safeFetch(`${import.meta.env.VITE_APP_API_BASE_URL}/api/archive/${date}/${state.index}`);
        loading.value = false;
        if (response?.ok) {
            const json = await response.json();
            currentWords.value = await decode(json.synonyms);
        } else {
            // 404 = no puzzle for this day (data gap / before launch).
            loadError.value = true;
            currentWords.value = [];
            console.error("Failed to fetch archive puzzle", response?.status);
        }
    }

    return {
        currentWords,
        loading,
        loadError,
        initialState() {
            return {
                score: state.index,
                hearts: state.hearts,
                over: state.over,
                guessedLetters: [...state.seenLetters],
                xp: 0,
            };
        },
        loadWords,
        async completed() {
            // Mirror the backend: completing a puzzle advances the run and banks a heart.
            state.index += 1;
            state.hearts = Math.min(state.hearts + 1, MAX_HEARTS);
            state.seenLetters = [];
            persist();
            await loadWords();
            return {game: state.index, hearts: state.hearts, xp: 0};
        },
        async gameOver() {
            state.over = true;
            persist();
        },
        async recordGuess(letters, hearts) {
            state.hearts = hearts;
            for (const letter of letters) {
                if (!state.seenLetters.includes(letter)) {
                    state.seenLetters.push(letter);
                }
            }
            persist();
        },
    };
}
